/**
 * The profile a player must complete before they can play — 04-profile.md §"Pakollinen profiilidata" (13.9.2026):
 * display name, avatar, gender and date of birth (PROFILE_BIRTHDATE_CONTRACT.md, 21.9.2026).
 *
 * The form uses profileComplete(); the server uses the same date validation and its own clock when saving a
 * birth date and checking Meet/Flirt eligibility. Legacy bands cannot complete a profile but still support the gate.
 */
import { z } from 'zod';
/**
 * The PLAYER's derived age group. Deliberately NOT the room-facing `AgeBand`, which carries 'any' and is what a host sets
 * on a room: widening that enum to add 16–17 would silently have made "16–17 only" a room anyone could create. A person
 * is 16–17 or older; a room is open to any age or to one band. Two meanings, two enums.
 * 16–17 is kept clearly apart from the adult bands (04-profile.md §5) because Meet and Flirt are 18+.
 */
export const PlayerAgeBand = z.enum(['16_17', '18_24', '25_34', '35_44', '45plus']);
export const PLAYER_AGE_BANDS = [
    { k: '16_17', label: '16–17' }, { k: '18_24', label: '18–24' }, { k: '25_34', label: '25–34' },
    { k: '35_44', label: '35–44' }, { k: '45plus', label: '45+' },
];
/** Legacy profiles only: their stored band decides the adult gate until a birth date is supplied. */
export const isAdultBand = (b) => !!b && b !== '16_17' && PlayerAgeBand.safeParse(b).success;
/** A real calendar date, with no time or timezone supplied by the client. Age limits use the caller's clock below. */
export const BirthDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.getUTCFullYear() > 0 && date.toISOString().slice(0, 10) === value;
}, 'Enter a valid date of birth (YYYY-MM-DD).');
/** UTC anniversaries; a 29 February birthday falls on 1 March in a non-leap year. */
function anniversary(birthDate, years) {
    const date = new Date(`${birthDate}T00:00:00.000Z`);
    date.setUTCFullYear(date.getUTCFullYear() + years);
    return date.getTime();
}
/** A user-facing reason, shared by the form and server. The server always supplies its own clock. */
export function birthDateError(birthDate, now = Date.now()) {
    if (!BirthDate.safeParse(birthDate).success)
        return 'Enter a valid date of birth (YYYY-MM-DD).';
    const today = new Date(now);
    today.setUTCHours(0, 0, 0, 0);
    const at = today.getTime();
    if (!Number.isFinite(at))
        return 'A valid current date is required.';
    if (anniversary(birthDate, 0) > at)
        return 'Date of birth cannot be in the future.';
    if (anniversary(birthDate, 120) < at)
        return 'Date of birth cannot be more than 120 years ago.';
    if (anniversary(birthDate, 16) > at)
        return 'You must be at least 16 years old to play SayNoWay.';
    return null;
}
/**
 * The moment this person BECAME an adult, or null if they have not yet. The null matters more than the number:
 * every caller treats `adultAt` as a yes/no — `!principal.value?.adultAt` opens the 18+ gate, `!!p?.adultAt` ticks
 * the adult box. Returning the 18th birthday unconditionally made that timestamp truthy for a 16-year-old, whose
 * birthday is simply in the future, so the gate stopped opening and a minor could walk into Flirt (found 21.9.2026
 * by var/tools/shot-gate-minor.mjs, which refused the release). A future anniversary is not evidence of adulthood.
 * Callers that genuinely want the future date should ask for it explicitly, not read it out of this.
 */
export function adultAtOf(birthDate, now = Date.now()) {
    if (!isAdult(birthDate, now))
        return null;
    return anniversary(birthDate, 18);
}
export function isAdult(birthDate, now = Date.now()) {
    return birthDateError(birthDate, now) === null && anniversary(birthDate, 18) <= Number(now);
}
/** Derived display/filter value; invalid and under-16 dates have no player band. */
export function bandOf(birthDate, now = Date.now()) {
    if (birthDateError(birthDate, now) !== null)
        return null;
    const at = Number(now);
    if (at < anniversary(birthDate, 18))
        return '16_17';
    if (at < anniversary(birthDate, 25))
        return '18_24';
    if (at < anniversary(birthDate, 35))
        return '25_34';
    if (at < anniversary(birthDate, 45))
        return '35_44';
    return '45plus';
}
/** The only gender values accepted for new profile writes (owner 21.9.2026). */
export const Gender = z.enum(['woman', 'man', 'other']);
export const GENDERS = [
    { k: 'woman', label: 'Female' }, { k: 'man', label: 'Male' }, { k: 'other', label: 'Other' },
];
/** Read legacy answers as Other without changing the stored value. Invalid or missing answers remain invalid. */
export function genderOf(value) {
    const parsed = Gender.safeParse(value === 'nonbinary' || value === 'unspecified' ? 'other' : value);
    return parsed.success ? parsed.data : null;
}
/** the four fields of 04-profile.md, in the order the screen asks for them */
export const REQUIRED_PROFILE_FIELDS = ['displayName', 'avatarId', 'gender', 'birthDate'];
/** which of the four are still missing or invalid — the screen uses this to mark the fields, the gate to fail a release */
export function missingProfileFields(d, now = Date.now()) {
    const out = [];
    const n = (d.displayName ?? '').trim();
    if (n.length < 1 || n.length > 8)
        out.push('displayName'); // DisplayName's own limit: the seat pills hold eight
    if (!(d.avatarId ?? '').trim())
        out.push('avatarId');
    if (genderOf(d.gender) === null)
        out.push('gender');
    // The screen is back on the drawn age chips (owner 21.9.2026: the graphic line may not change on our initiative,
    // and the date-of-birth control changed it). A profile is therefore complete with EITHER a valid birth date or a
    // valid age band — the birth date stays the better answer and the server still prefers it, so nothing that has one
    // loses it, and the field can come back the moment its artwork exists.
    if (birthDateError(d.birthDate, now) !== null && !PlayerAgeBand.safeParse(d.ageBand).success)
        out.push('birthDate');
    return out;
}
/** CONTINUE is enabled only when this is true (04-profile.md §6) */
export const profileComplete = (d, now = Date.now()) => missingProfileFields(d, now).length === 0;
/** full years lived at `now` for a stored YYYY-MM-DD birth date; null when there is none or it does not parse */
export function ageYears(birthDate, now = Date.now()) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate ?? '');
    if (!m)
        return null;
    const d = now instanceof Date ? now : new Date(now);
    let years = d.getUTCFullYear() - Number(m[1]);
    if (d.getUTCMonth() + 1 < Number(m[2]) || (d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() < Number(m[3])))
        years -= 1;
    return years >= 0 && years < 130 ? years : null;
}
//# sourceMappingURL=profile.js.map